import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login } from "../actions"; // アクションをインポート
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CssBaseline from "@mui/material/CssBaseline";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Alert from "@mui/material/Alert"; // Alertをインポート
import { CircularProgress } from "@mui/material";

const LoginCheckbox = styled(Checkbox)(({ theme }) => ({
  "&.Mui-checked": {
    color: "#EF6266", // チェック時の色
  },
  "&.Mui-checked:hover": {
    backgroundColor: "rgba(239, 98, 102, 0.1)", // ホバー時の色
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#EF6266",
}));

const LoginTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: "#72574E", // ホバー時のボーダー色
    },
    "&.Mui-focused fieldset": {
      borderColor: "#EF6266", // フォーカス時のボーダー色
    },
  },
}));

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
  },
}));

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.isAuthenticated);
  const userRole = useSelector((state) => state.role);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedUsername = localStorage.getItem("username");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    // ログイン済みの他のタブでの状態を反映してリダイレクト
    // if (isAuthenticated) {
    //   navigate("/host/guest-manage");
    // }
    if (isAuthenticated) {
      // ロールに基づいてリダイレクト先を変更
      if (userRole === "admin") {
        console.log("Im admin.");
        navigate("/host/guest-manage");
      } else {
        navigate("/host/reception");
      }
    }

    // const handleStorageChange = (event) => {
    //   if (event.key === "token" && event.newValue) {
    //     const decodedToken = jwtDecode(event.newValue);
    //     dispatch(login(decodedToken)); // 他のタブでのログイン状態に合わせる
    //     if (decodedToken.role === "receptionist") {
    //       navigate("/host/reception"); // Receptionistの場合は別のページへ
    //     } else {
    //       navigate("/host/guest-manage"); // Guest-manageページにリダイレクト
    //     }
    //   }
    // };

    // window.addEventListener("storage", handleStorageChange);
    // return () => window.removeEventListener("storage", handleStorageChange);
  }, [isAuthenticated, navigate, dispatch, userRole]);

  const handleLogin = async () => {
    setLoading(true);

    // try {
    //   dispatch(login(username, password, rememberMe))
    //   console.log("isAuthenticated", isAuthenticated)
    //   const token = localStorage.getItem("token");
    //   if (token) {
    //     const decodedToken = jwtDecode(token); // トークンをデコード
    //     const role = decodedToken.role; // ユーザーのロールを取得
    //     if (rememberMe) {
    //       localStorage.setItem("username", username);
    //     } else {
    //       localStorage.removeItem("username");
    //     }
    //     // navigate("/host/guest-manage");
    //     setLoading(false)
    //     if (role === "admin") {
    //       navigate("/host/guest-manage");
    //     } else if(role === "receptionist")  {
    //       navigate("/host/reception");
    //     }else{}
    //   }
    // } catch (error) {
    //   setLoading(false)
    //   setErrorMessage("ログインに失敗しました。"); // エラーメッセージを設定
    // }

    dispatch(login(username, password, rememberMe))
      .then(({ token, role }) => {
        console.log("isAuthenticated after login", isAuthenticated);

        if (rememberMe) {
          localStorage.setItem("username", username);
        } else {
          localStorage.removeItem("username");
        }

        setLoading(false);

        if (role === "admin") {
          navigate("/host/guest-manage");
        } else if (role === "receptionist") {
          navigate("/host/reception");
        }
      })
      .catch((error) => {
        console.error("Login error:", error);
        setLoading(false);
        setErrorMessage("ログインに失敗しました。");
      });
  };

  return (
    <div className="login-bg">
      <CssBaseline enableColorScheme />

      {loading ? (
        <Box
          sx={{
            position: "fixed", // 画面に固定
            top: "50%", // 垂直方向の中央
            left: "50%", // 水平方向の中央
            transform: "translate(-50%, -50%)", // 自身の中心を基準に中央に配置
            zIndex: 1100, // 他の要素の上に表示（必要に応じて調整）
          }}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      ) : (
        <SignInContainer direction="column" justifyContent="space-between">
          <Card variant="outlined" className="login-card">
            <Typography
              gutterBottom
              component="h1"
              variant="h4"
              sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
              className="login-label"
            >
              Login
            </Typography>
            <Box
              component="form"
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
              }}
            >
              <FormControl>
                <FormLabel htmlFor="username" className="login-label">
                  ユーザー名
                </FormLabel>
                <LoginTextField
                  id="username"
                  name="username"
                  placeholder="ユーザー名を入力"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setUsername(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="password" className="login-label">
                  パスワード
                </FormLabel>
                <LoginTextField
                  name="password"
                  placeholder="••••••"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  required
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormControl>
              {/* <FormControlLabel
                control={
                  <LoginCheckbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label="Remember me"
              /> */}
              <LoginButton
                type="button"
                fullWidth
                variant="contained"
                onClick={handleLogin}
                className="login-button"
                sx={{ mt: 2 }}
              >
                Login
              </LoginButton>
              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}{" "}
              {/* エラーメッセージの表示 */}
            </Box>
          </Card>
        </SignInContainer>
      )}
    </div>
  );
};

export default Login;
